import cx from 'classnames';

import { Button, Icon } from '@sb/design-system';
import { useToast } from '@sbrc/hooks';

interface RestoreErrorDetailProps {
  message: string;
}

/**
 * The reason a restore failed, in a form someone can actually act on.
 *
 * These are engine errors — a failed `nsenter`/`tar` carries the whole
 * command line — so they are long, full of unbreakable paths, and the first
 * thing support asks for. Centred prose clipped to the modal's width was
 * unreadable and unquotable: this is left-aligned, monospace, wraps mid-path,
 * scrolls rather than overflows, stays selectable, and offers a copy button.
 */
export function RestoreErrorDetail({ message }: RestoreErrorDetailProps) {
  const { setToast } = useToast();

  // navigator.clipboard exists only in a secure context; on a robot reached
  // over plain http there is nothing to call, so fall back to selecting the
  // text by hand rather than showing a button that cannot work.
  const canCopy = Boolean(navigator.clipboard);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);

      setToast({ kind: 'success', message: 'Error copied to clipboard' });
    } catch {
      setToast({ kind: 'error', message: 'Could not copy the error' });
    }
  };

  return (
    <section
      className={cx('tw-flex', 'tw-flex-col', 'tw-gap-4', 'tw-w-full')}
      data-testid="restore-confirmation-error-detail-region"
    >
      <div className={cx('tw-flex', 'tw-items-center', 'tw-justify-between')}>
        <span className="tw-text-13 tw-text-label-secondary">Details</span>

        {canCopy && (
          <Button
            size={32}
            variant="Flat"
            className="tw-rounded-6"
            data-testid="restore-confirmation-error-copy-button"
            onClick={onCopy}
          >
            <Icon kind="squareOnSquare" className="tw-mr-2" />
            <span>Copy</span>
          </Button>
        )}
      </div>

      <pre
        className={cx(
          'tw-text-13',
          'tw-font-mono',
          'tw-text-left',
          'tw-text-label-primary',
          'tw-bg-fill-primary',
          'tw-rounded-10',
          'tw-p-12',
          'tw-max-h-[168px]',
          'tw-overflow-auto',
          'tw-whitespace-pre-wrap',
          // Engine errors are mostly paths and flags with no spaces to break
          // on; without this they force the modal wider than the screen.
          'tw-break-all',
          'tw-select-text',
        )}
        data-testid="restore-confirmation-error-detail"
      >
        {message}
      </pre>
    </section>
  );
}
